import React, { useState, useEffect } from "react";
import { Container, Paper, Typography, Box, Grid, Link, Divider, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import { headingBlue, yellowish, pureWhite, lightGrey, maroon } from "../assets/colors";
import BackArrow from "./BackArrow";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("");

  const sections = [
    { id: "intro", label: "Overview", icon: <InfoOutlinedIcon fontSize="small" /> },
    { id: "collect", label: "Information We Collect", icon: <SecurityOutlinedIcon fontSize="small" /> },
    { id: "use", label: "How We Use Information", icon: <GavelOutlinedIcon fontSize="small" /> },
    { id: "thirdparty", label: "Third-Party Services", icon: <InfoOutlinedIcon fontSize="small" /> },
    { id: "sharing", label: "Data Sharing", icon: <SecurityOutlinedIcon fontSize="small" /> },
    { id: "security", label: "Data Security", icon: <SecurityOutlinedIcon fontSize="small" /> },
    { id: "retention", label: "Data Retention", icon: <GavelOutlinedIcon fontSize="small" /> },
    { id: "rights", label: "Your Rights", icon: <GavelOutlinedIcon fontSize="small" /> },
    { id: "children", label: "Children's Privacy", icon: <InfoOutlinedIcon fontSize="small" /> },
    { id: "changes", label: "Policy Changes", icon: <GavelOutlinedIcon fontSize="small" /> },
    { id: "contact", label: "Contact Us", icon: <ContactSupportOutlinedIcon fontSize="small" /> },
  ];

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#f4f6f8",
        minHeight: "100vh",
        pb: 8,
        fontFamily: "Outfit, Inter, Roboto, sans-serif",
      }}
    >
      {/* Premium Gradient Header Header Bar */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${headingBlue} 0%, #1a365d 100%)`,
          color: pureWhite,
          py: { xs: 4, md: 6 },
          px: 3,
          textAlign: "center",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          position: "relative",
          mb: 4,
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <BackArrow path="/" label="Back" style={{ color: pureWhite }} />
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "3rem" },
              letterSpacing: "-0.02em",
              mb: 1,
              animation: "fadeInDown 0.8s ease-out",
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: yellowish,
              fontWeight: 500,
              opacity: 0.9,
              letterSpacing: "0.05em",
            }}
          >
            Effective Date: July 10, 2026
          </Typography>
        </Container>
      </Box>

      {/* Main Grid Content */}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Navigation Sidebar (Desktop) */}
          <Grid item xs={12} md={3} sx={{ display: { xs: "none", md: "block" } }}>
            <Box
              sx={{
                position: "sticky",
                top: "24px",
                backgroundColor: pureWhite,
                borderRadius: "16px",
                p: 2,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.04)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: "#6b7280",
                  letterSpacing: "0.08em",
                  mb: 2,
                  px: 1,
                }}
              >
                Table of Contents
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {sections.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <Button
                      key={section.id}
                      onClick={() => handleScrollTo(section.id)}
                      startIcon={section.icon}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        py: 1,
                        px: 1.5,
                        borderRadius: "8px",
                        fontSize: "0.85rem",
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? pureWhite : "#4b5563",
                        backgroundColor: isActive ? headingBlue : "transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: isActive ? headingBlue : "rgba(41, 75, 130, 0.08)",
                          color: isActive ? pureWhite : headingBlue,
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {section.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Grid>

          {/* Privacy Policy Document Sections */}
          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "24px",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
                border: "1px solid rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              {/* Introduction */}
              <Box id="intro" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  WORK Portal Privacy Policy
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#374151",
                    lineHeight: 1.7,
                    fontSize: "0.95rem",
                    mb: 2,
                  }}
                >
                  <strong>WORK Portal</strong> (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the WORK Portal mobile application.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#374151",
                    lineHeight: 1.7,
                    fontSize: "0.95rem",
                  }}
                >
                  By using the WORK Portal app, you agree to the practices described in this Privacy Policy.
                </Typography>
              </Box>

              <Divider />

              {/* Information We Collect */}
              <Box id="collect" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2.5,
                  }}
                >
                  Information We Collect
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#4b5563", mb: 2, fontSize: "0.9rem" }}
                >
                  We may collect the following types of information:
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        backgroundColor: "#f8fafc",
                        borderRadius: "12px",
                        height: "100%",
                        border: "1px solid rgba(0,0,0,0.03)",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: headingBlue, mb: 1 }}
                      >
                        Personal Information
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ pl: 2, m: 0, fontSize: "0.85rem", color: "#374151" }}
                      >
                        <li>Mobile phone number (used for OTP authentication)</li>
                        <li>Name (if provided by your organization)</li>
                        <li>Employee or worker identification details (if applicable)</li>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        backgroundColor: "#f8fafc",
                        borderRadius: "12px",
                        height: "100%",
                        border: "1px solid rgba(0,0,0,0.03)",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: headingBlue, mb: 1 }}
                      >
                        Device & Usage Information
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ pl: 2, m: 0, fontSize: "0.85rem", color: "#374151" }}
                      >
                        <li>Device model & operating system version</li>
                        <li>App version & IP address</li>
                        <li>Device identifiers</li>
                        <li>Crash reports and diagnostic information</li>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* How We Use Your Information */}
              <Box id="use" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  How We Use Your Information
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  We use the collected information to:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 3,
                    fontSize: "0.95rem",
                    color: "#374151",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <li>Verify your identity using One-Time Password (OTP)</li>
                  <li>Provide secure access to your account</li>
                  <li>Improve the performance and functionality of the application</li>
                  <li>Maintain the security of our services</li>
                  <li>Respond to support requests</li>
                  <li>Comply with legal and regulatory obligations</li>
                </Box>
              </Box>

              <Divider />

              {/* Third-Party Services */}
              <Box id="thirdparty" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Third-Party Services
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  WORK Portal uses trusted third-party services, including Google Firebase, to provide authentication and other essential services.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 3, fontSize: "0.95rem" }}
                >
                  These services may collect and process information in accordance with their own privacy policies.
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: "rgba(41, 75, 130, 0.02)",
                    borderColor: "rgba(41, 75, 130, 0.1)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: headingBlue, mb: 0.5 }}
                  >
                    Google Privacy Policy:
                  </Typography>
                  <Link
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: headingBlue,
                      fontWeight: 600,
                      wordBreak: "break-all",
                      textDecoration: "underline",
                      "&:hover": { color: maroon },
                    }}
                  >
                    https://policies.google.com/privacy
                  </Link>
                </Paper>
              </Box>

              <Divider />

              {/* Data Sharing */}
              <Box id="sharing" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Data Sharing
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  We do not sell, rent, or trade your personal information.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#4b5563", mb: 2, fontWeight: 600, fontSize: "0.9rem" }}
                >
                  Your information may be shared only:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 3,
                    fontSize: "0.95rem",
                    color: "#374151",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <li>With trusted service providers who help operate the application</li>
                  <li>When required by law or legal process</li>
                  <li>To protect our rights, users, or the security of the application</li>
                  <li>With your organization where necessary to provide the service</li>
                </Box>
              </Box>

              <Divider />

              {/* Data Security */}
              <Box id="security" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Data Security
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  We use appropriate technical and organizational measures to protect your information, including:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 3,
                    fontSize: "0.95rem",
                    color: "#374151",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <li>Secure HTTPS communication</li>
                  <li>Authentication through Firebase</li>
                  <li>Restricted access to user data</li>
                  <li>Encryption during data transmission</li>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#fffbeb",
                    borderLeft: `4px solid ${yellowish}`,
                    borderRadius: "4px",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#92400e", fontStyle: "italic" }}>
                    While we take reasonable precautions, no method of electronic transmission or storage is completely secure.
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              {/* Data Retention */}
              <Box id="retention" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Data Retention
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", lineHeight: 1.7, fontSize: "0.95rem" }}
                >
                  We retain your personal information only for as long as necessary to provide our services or to comply with legal obligations. When data is no longer required, it is securely deleted or anonymized where appropriate.
                </Typography>
              </Box>

              <Divider />

              {/* Your Rights */}
              <Box id="rights" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Your Rights
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  Depending on your location and applicable laws, you may have the right to:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 3,
                    fontSize: "0.95rem",
                    color: "#374151",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Withdraw consent where applicable</li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", fontSize: "0.95rem" }}
                >
                  To exercise these rights, please contact us using the details below.
                </Typography>
              </Box>

              <Divider />

              {/* Children's Privacy */}
              <Box id="children" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Children's Privacy
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 2, fontSize: "0.95rem" }}
                >
                  WORK Portal is intended for users aged 18 years and above. We do not knowingly collect personal information from children under the age of 13.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", fontSize: "0.95rem" }}
                >
                  If we become aware that personal information has been collected from a child without appropriate consent, we will take steps to delete that information.
                </Typography>
              </Box>

              <Divider />

              {/* Changes to This Privacy Policy */}
              <Box id="changes" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2,
                  }}
                >
                  Changes to This Privacy Policy
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", lineHeight: 1.7, fontSize: "0.95rem" }}
                >
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated Effective Date. Continued use of the application after changes become effective constitutes acceptance of the updated Privacy Policy.
                </Typography>
              </Box>

              <Divider />

              {/* Contact Us */}
              <Box id="contact" sx={{ scrollMarginTop: "24px" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: headingBlue,
                    mb: 2.5,
                  }}
                >
                  Contact Us
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#374151", mb: 3, fontSize: "0.95rem" }}
                >
                  If you have any questions or concerns about this Privacy Policy, please contact us:
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    backgroundColor: "#f8fafc",
                    borderColor: "rgba(0,0,0,0.08)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>
                        Email Support
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: headingBlue, mt: 0.5 }}>
                        <Link
                          href="mailto:support@worktrust.in"
                          sx={{
                            color: headingBlue,
                            textDecoration: "none",
                            "&:hover": { color: maroon, textDecoration: "underline" },
                          }}
                        >
                          support@worktrust.in
                        </Link>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>
                        Official Website
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: headingBlue, mt: 0.5 }}>
                        <Link
                          href="https://worktrust.in"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: headingBlue,
                            textDecoration: "none",
                            "&:hover": { color: maroon, textDecoration: "underline" },
                          }}
                        >
                          https://worktrust.in
                        </Link>
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
